import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ClockIcon,
  CheckIcon,
  CheckCircleIcon,
  XMarkIcon,
  DocumentTextIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/solid";
import { StarIcon, TrophyIcon } from "@heroicons/react/24/outline";
import { Avatar, Chip } from "@heroui/react";
import { toast } from "sonner";
import { DealResponseDto } from "../../types/types";

type StepKey =
  | "PROPOSED"
  | "ACCEPTED"
  | "CONFIRMED"
  | "WAITING_FOR_RESULT"
  | "RESULT_UPDATED"
  | "RESULT_CONFIRMED"
  | "POST_PAYMENT"
  | "PRE_PAYMENT_RELEASED"
  | "POST_PAYMENT_RELEASED"
  | "FIGHTER_RATED"
  | "MANAGER_RATED"
  | "FINISHED"
  | "PAYMENT_REFUNDED"
  | "EXPIRED"
  | "DISPUTE"
  | "CANCELLED"
  | "REJECTED";

type Tone = "gray" | "green" | "red";

type StatusConfigEntry = {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  color: Tone;
};

const statusConfig: Record<StepKey, StatusConfigEntry> = {
  PROPOSED: { icon: ClockIcon, color: "gray" },
  ACCEPTED: { icon: CheckIcon, color: "gray" },
  CONFIRMED: { icon: CheckCircleIcon, color: "green" },
  WAITING_FOR_RESULT: { icon: ClockIcon, color: "gray" },
  RESULT_UPDATED: { icon: CheckIcon, color: "gray" },
  FIGHTER_RATED: { icon: StarIcon, color: "gray" },
  RESULT_CONFIRMED: { icon: CheckCircleIcon, color: "green" },
  MANAGER_RATED: { icon: StarIcon, color: "gray" },
  FINISHED: { icon: TrophyIcon, color: "green" },
  DISPUTE: { icon: DocumentTextIcon, color: "red" },
  CANCELLED: { icon: XMarkIcon, color: "red" },
  REJECTED: { icon: XMarkIcon, color: "red" },
  PRE_PAYMENT_RELEASED: { icon: CheckIcon, color: "gray" },
  POST_PAYMENT: { icon: ClockIcon, color: "gray" },
  POST_PAYMENT_RELEASED: { icon: CheckIcon, color: "green" },
  PAYMENT_REFUNDED: { icon: XMarkIcon, color: "red" },
  EXPIRED: { icon: XMarkIcon, color: "red" },
};

const colorClasses: Record<Tone, { border: string; text: string }> = {
  gray: { border: "border-gray-500", text: "text-gray-600" },
  green: { border: "border-green-500", text: "text-green-600" },
  red: { border: "border-red-500", text: "text-red-600" },
};

const isFighterAccepted = (deal: DealResponseDto): boolean =>
  ["RESULT_CONFIRMED", "FIGHTER_RATED", "MANAGER_RATED", "FINISHED"].includes(
    deal.status ?? "",
  );

const statusLabels: Record<StepKey, string> = {
  PROPOSED: "progressStatusProposed",
  ACCEPTED: "progressStatusAccepted",
  CONFIRMED: "progressStatusPaymentConfirmed",
  WAITING_FOR_RESULT: "progressStatusWaitingForResult",
  RESULT_UPDATED: "progressStatusResultUpdated",
  FIGHTER_RATED: "progressStatusFighterRated",
  RESULT_CONFIRMED: "progressStatusResultConfirmed",
  MANAGER_RATED: "progressStatusManagerRated",
  FINISHED: "progressStatusFinished",
  DISPUTE: "progressStatusDisputed",
  CANCELLED: "progressStatusCancelled",
  REJECTED: "progressStatusRejected",
  PRE_PAYMENT_RELEASED: "progressStatusPrePaymentReleased",
  POST_PAYMENT: "progressStatusPostPayment",
  POST_PAYMENT_RELEASED: "progressStatusPostPaymentReleased",
  PAYMENT_REFUNDED: "progressStatusPaymentRefunded",
  EXPIRED: "progressStatusExpired",
};

const getStatusLabel = (step: StepKey, t: any) => {
  return t(statusLabels[step]);
};

const getActivityStatusLabel = (status: string | undefined, t: any) => {
  if (status && status in statusLabels) {
    return getStatusLabel(status as StepKey, t);
  }
  return (status ?? "").replace(/_/g, " ");
};

interface ActionButtonConfig {
  label: string;
  labelKey: string;
  tab: string;
}

const getActivityActionButton = (
  activity: any,
  currentUserRole: "MANAGER" | "FIGHTER" | string | undefined,
  allActivities: any[] = [],
): ActionButtonConfig | null => {
  const status: string = activity.status ?? "";

  const skipProcessedCheck = status === "WAITING_FOR_RESULT";

  if (activity.processed && !skipProcessedCheck) return null;

  switch (status) {
    case "ACCEPTED":
      if (currentUserRole === "FIGHTER") {
        return {
          label: "Accept Or Decline Deal",
          labelKey: "progressActionAcceptOrDeclineDeal",
          tab: "result",
        };
      }
      break;

    case "CONFIRMED":
      if (currentUserRole === "MANAGER") {
        return {
          label: "Confirm Payment",
          labelKey: "progressActionConfirmPayment",
          tab: "componentPayments",
        };
      }
      break;

    case "WAITING_FOR_RESULT": {
      const resultAlreadyUpdated = allActivities.some(
        (act: any) => act.status === "RESULT_UPDATED",
      );
      if (resultAlreadyUpdated) return null;
      if (currentUserRole === "MANAGER") {
        return {
          label: "Update Result",
          labelKey: "progressActionUpdateResult",
          tab: "result",
        };
      }
      break;
    }
    case "RESULT_UPDATED":
      if (currentUserRole === "MANAGER") {
        return {
          label: "Update Result",
          labelKey: "progressActionUpdateResult",
          tab: "result",
        };
      }
      break;

    case "RESULT_CONFIRMED":
      if (currentUserRole === "FIGHTER") {
        return {
          label: "Confirm Result",
          labelKey: "progressActionConfirmResult",
          tab: "result",
        };
      }
      break;

    case "FIGHTER_RATED":
      if (currentUserRole === "FIGHTER") {
        return {
          label: "Submit Rating",
          labelKey: "progressActionSubmitRating",
          tab: "result",
        };
      }
      break;

    case "MANAGER_RATED":
      if (currentUserRole === "MANAGER") {
        return {
          label: "Submit Rating",
          labelKey: "progressActionSubmitRating",
          tab: "result",
        };
      }
      break;

    case "PRE_PAYMENT_RELEASED":
      if (currentUserRole === "MANAGER") {
        return {
          label: "Release Payment",
          labelKey: "progressActionReleasePayment",
          tab: "componentPayments",
        };
      }
      break;

      case "POST_PAYMENT":
  if (currentUserRole === "MANAGER") {
    return {
      label: "Process Post Payment",
      labelKey: "progressActionProcessPostPayment",
      tab: "componentPayments",
    };
  }
  break;
    default:
      break;
  }

  return null;
};

interface GeneralActivityTabProps {
  deal: DealResponseDto;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  t: any;
  formData?: any;
  renderDynamicObject?: (objectData: any, skip?: number) => React.ReactNode;
  currentUserRole?: "MANAGER" | "FIGHTER" | string;
  tr: (value?: string) => string;
  refreshDeal?: () => void;
}

const GeneralActivityTab: React.FC<GeneralActivityTabProps> = ({
  deal,
  activeTab,
  setActiveTab,
  t,
  formData,
  renderDynamicObject,
  currentUserRole,
  tr,
  refreshDeal,
}) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const fighterAccepted = isFighterAccepted(deal);
  const shouldDisableProgressActions = deal.status === "CANCELLED";
  const shouldHideProgressActions = deal.status === "FINISHED";

  const shouldNavigateToChat = (activity: any): boolean =>
    activity.status === "ACCEPTED" && currentUserRole === "FIGHTER";

  // Helper function to format activity message dynamically
  const getActivityDisplayMessage = useCallback((activity: any) => {
    // 1. If backend still sends an old explicit message, fallback to it
    if (activity.message) {
      return tr(activity.message);
    }

    // 2. Extract message args (JSON string or Array)
    let args: string[] = [];
    const rawArgs = activity.processed ? activity.messageArgs : activity.pendingMessageArgs;
    if (typeof rawArgs === "string" && rawArgs.trim()) {
      try {
        args = JSON.parse(rawArgs);
      } catch {
        args = [];
      }
    } else if (Array.isArray(rawArgs)) {
      args = rawArgs;
    }

    // 3. Determine i18n key
    let key = activity.i18nKey || activity.status;
    if (!activity.processed && key && key !== "PROPOSED" && !key.endsWith("_PENDING")) {
      key = `${key}_PENDING`;
    }

    // 4. Translate using i18n t() - (Removed 'viewDeal.' prefix)
    const translated = t(`activity_${key}`, {
      0: args[0] || "",
      1: args[1] || "",
      2: args[2] || "",
      3: args[3] || "",
      defaultValue: "",
    });

    if (translated) {
      return translated;
    }

    return key ? key.replace(/_/g, " ") : "";
  }, [t, tr]);

  const getChatTargetUserId = (): number | null => {
    if (currentUserRole === "FIGHTER") {
      return (
        (deal as any).initiatedBy?.id ?? (deal as any).createdBy?.id ?? null
      );
    }
    if (currentUserRole === "MANAGER") {
      return (deal as any).acceptedBy?.id ?? null;
    }
    return null;
  };

  const shouldSwitchToComponentPayments = (activity: any): boolean =>
    activity.status === "CONFIRMED" && currentUserRole === "MANAGER";

  const progressSteps = useMemo<StepKey[]>(() => {
    const status = deal.status as StepKey | undefined;

    const activityStatuses = new Set(
      (deal.activityResponse || []).map((a: any) => a.status),
    );

    const hasPostPayment = activityStatuses.has("POST_PAYMENT");
    const hasPostPaymentReleased = activityStatuses.has(
      "POST_PAYMENT_RELEASED",
    );
    const hasPaymentRefunded = activityStatuses.has("PAYMENT_REFUNDED");

    const steps: StepKey[] = [
      "PROPOSED",
      "ACCEPTED",
      "CONFIRMED",
      "WAITING_FOR_RESULT",
      "RESULT_UPDATED",
      "MANAGER_RATED",
      "RESULT_CONFIRMED",
      "FIGHTER_RATED",
    ];

    if (hasPostPayment) steps.push("POST_PAYMENT");
    steps.push("PRE_PAYMENT_RELEASED");
    if (hasPostPaymentReleased) steps.push("POST_PAYMENT_RELEASED");
    if (hasPaymentRefunded) steps.push("PAYMENT_REFUNDED");
    steps.push("FINISHED");

    if (status === "REJECTED") return ["PROPOSED", "REJECTED"];

    if (status === "CANCELLED") {
      const hasAccepted = (deal.activityResponse || []).some(
        (a: any) => a.status === "ACCEPTED",
      );
      return hasAccepted
        ? ["PROPOSED", "ACCEPTED", "CANCELLED"]
        : ["PROPOSED", "CANCELLED"];
    }

    if (status === "DISPUTE") {
      return [
        "PROPOSED",
        "ACCEPTED",
        "CONFIRMED",
        "WAITING_FOR_RESULT",
        "RESULT_UPDATED",
        "DISPUTE",
      ];
    }

    return steps;
  }, [deal.activityResponse, deal.status]);

  const activeIndex = useMemo(() => {
    const activities = deal.activityResponse || [];

    const processed = activities.filter((act: any) => act.processed);

    if (processed.length > 0) {
      const lastProcessed = processed[processed.length - 1];

      const idx = progressSteps.indexOf(lastProcessed.status as StepKey);
      if (idx !== -1) return idx;
    }
    const idx = progressSteps.indexOf((deal.status ?? "") as StepKey);
    return idx === -1 ? 0 : idx;
  }, [deal.status, deal.activityResponse, progressSteps]);




  return (
    <>
      {activeTab === "general" && (
        <div className="space-y-6 text-left">
          <div
            className="
              rounded-lg border p-4
              text-[14px] leading-6 text-gray-700
              [&_label]:text-[14px]
              [&_label]:font-medium
              [&_label]:text-gray-500
              [&_p]:text-[14px]
              [&_span]:text-[14px]
              [&_li]:text-[14px]
              [&_div]:text-[14px]
              [&_input]:text-[14px]
              [&_select]:text-[14px]
              [&_textarea]:text-[14px]
            "
          >
            {formData && renderDynamicObject ? (
              renderDynamicObject(
                (() => {
                  if (!formData || typeof formData !== "object") return formData;
                  const { document_tracker, ...rest } = formData;
                  return rest;
                })(),
              )
            ) : (
              <div>{t("noFormData")}</div>
            )}
          </div>
        </div>
      )}

      {activeTab === "progress" && (
        <div className="space-y-8">
          <div className="mx-auto max-w-4xl">
            <div className="space-y-6">
              {progressSteps.map((step, idx) => {
                const activities = (deal.activityResponse || []).filter(
                  (act: any) => {
                    if (step === "WAITING_FOR_RESULT") {
                      return act.status === "WAITING_FOR_RESULT";
                    }
                    if (step === "RESULT_UPDATED") {
                      return act.status === "RESULT_UPDATED";
                    }
                    return act.status === step;
                  },
                );

                const hasThisStepActivity = activities.length > 0;
                const isStepProcessed = activities.some(
                  (act: any) => act.processed === true,
                );

                const completed = isStepProcessed;

                const current = hasThisStepActivity && !isStepProcessed;
                const cfg = statusConfig[step];
                const isDispute = step === "DISPUTE";
                const isTerminalFailure =
                  step === "REJECTED" || step === "CANCELLED";
                const hasActivity = activities.length > 0;
                const isFuture =
                  !hasActivity && !isTerminalFailure && !isDispute;
                const isLastStep = idx === progressSteps.length - 1;

                const IconComp = isTerminalFailure
                  ? XMarkIcon
                  : isDispute
                    ? DocumentTextIcon
                    : completed
                      ? CheckCircleIcon
                      : cfg.icon;

                const iconTone: Tone =
                  isTerminalFailure || isDispute
                    ? "red"
                    : completed
                      ? "green"
                      : "gray";

                const stepTone = colorClasses[iconTone];

                const borderClass =
                  current && !completed && !isTerminalFailure && !isDispute
                    ? "border-gray-400"
                    : stepTone.border;

                return (
                  <div
                    key={step}
                    style={{
                      opacity: isFuture ? 0.4 : 1,
                      transition: "opacity 0.2s",
                    }}
                    className="grid grid-cols-[88px_minmax(0,1fr)] items-stretch gap-5 sm:grid-cols-[96px_minmax(0,1fr)] sm:gap-6"
                  >
                    <div className="flex h-full min-h-0 flex-col items-center">
                      <div
                        className={`z-10 flex h-11 w-11 items-center justify-center rounded-full border-4 bg-white shadow-md ${borderClass}`}
                      >
                        <IconComp className={`h-6 w-6 ${stepTone.text}`} />
                      </div>

                      <div
                        className={`mt-3 w-full max-w-[84px] text-center text-[11px] uppercase tracking-wider leading-4 break-words sm:max-w-[88px] ${
                          isTerminalFailure || isDispute
                            ? "font-semibold text-red-600"
                            : current
                            ? "font-semibold text-gray-700"
                            : completed
                              ? "font-semibold text-green-600"
                              : "font-normal text-gray-400"
                        }`}
                      >
                        {getStatusLabel(step, t)}
                      </div>

                      {!isLastStep && (
                        <div
                          className={`mt-3 w-px flex-1 ${
                            completed ? "bg-green-300" : "bg-gray-200"
                          }`}
                        />
                      )}
                    </div>

                    <div className="min-w-0 pb-6 sm:pb-8">
                      {activities.length > 0 ? (
                        <div className="space-y-3">
                          {activities.map((activity: any) => {
                            const actionBtn = shouldHideProgressActions
                              ? null
                              : getActivityActionButton(
                                  activity,
                                  currentUserRole,
                                  deal.activityResponse || [],
                                );

                            return (
                              <div
                                key={activity.id}
                                className={`w-full max-w-2xl rounded-xl border p-5 ${
                                  isDispute || isTerminalFailure
                                    ? "border-red-200 bg-red-50"
                                    : "border-gray-200 bg-gray-50"
                                }`}
                              >
                                <div className="flex items-start gap-4">
                                  <Avatar
                                    size="sm"
                                    src={activity.performedBy?.profileImage}
                                    name={`${activity.performedBy?.firstName?.[0] || ""}${activity.performedBy?.lastName?.[0] || ""}`}
                                    className="shrink-0"
                                  />

                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-start justify-between gap-3">
                                      <p
                                        className="min-w-0 font-medium text-gray-900 cursor-pointer hover:underline"
                                        onClick={() => {
                                          if (activity.performedBy?.id) {
                                            navigate(
                                              `/profile?profileId=${activity.performedBy.id}`,
                                            );
                                          }
                                        }}
                                      >
                                        {activity.performedBy
                                          ? `${activity.performedBy.firstName} ${activity.performedBy.lastName}`
                                          : t("systemUser")}
                                      </p>

                                      {actionBtn && (
                                        <button
                                          onClick={() => {
                                            if (shouldDisableProgressActions) {
                                              return;
                                            }

                                            if (
                                              shouldNavigateToChat(activity)
                                            ) {
                                              const targetUserId =
                                                getChatTargetUserId();
                                              if (targetUserId !== null) {
                                                navigate(
                                                  `/chat?userId=${targetUserId}&dealId=${deal.id}`,
                                                );
                                              }
                                              return;
                                            }

                                            if (
                                              shouldSwitchToComponentPayments(
                                                activity,
                                              )
                                            ) {
                                              setActiveTab("componentPayments");
                                              return;
                                            }

                                            setActiveTab(actionBtn.tab);
                                          }}
                                          className={[
                                            "inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition",
                                            shouldDisableProgressActions
                                              ? "cursor-not-allowed opacity-50"
                                              : "hover:border-gray-400 hover:bg-gray-100 active:scale-95",
                                          ].join(" ")}
                                          disabled={shouldDisableProgressActions}
                                        >
                                          {t(actionBtn.labelKey)}
                                          <ArrowRightIcon className="h-3.5 w-3.5" />
                                        </button>
                                      )}
                                    </div>

                                    <p
  className="mt-2 break-words text-sm leading-relaxed text-gray-700"
  dangerouslySetInnerHTML={{
    __html: getActivityDisplayMessage(activity),
  }}
/>

                                    <span className="mt-3 block text-xs text-gray-500">
                                      {new Date(
                                        activity.updatedAt + "Z",
                                      ).toLocaleString()}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="pt-4 text-sm italic text-gray-400">
                          {isTerminalFailure && current
                            ? t("dealRejected")
                            : t("noActivityForStep")}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {activeTab === "activity" && (
        <div className="space-y-4">
          {!deal.activityResponse || deal.activityResponse.length === 0 ? (
            <p className="text-sm text-gray-500">
              {t("noActivityYet")}
            </p>
          ) : (
            <div className="divide-y divide-gray-200 overflow-hidden rounded-md border border-gray-200">
              {deal.activityResponse.map((activity) => {
                const hasUser = !!activity.performedBy;

                const actorName = hasUser
                  ? `${activity.performedBy?.firstName || ""} ${activity.performedBy?.lastName || ""}`.trim()
                  : t("systemUser");

                const actorInitials = hasUser
                  ? `${activity.performedBy?.firstName?.[0] || ""}${activity.performedBy?.lastName?.[0] || ""}`.trim() ||
                    "U"
                  : "S";

                const avatarSrc = hasUser
                  ? activity.performedBy?.profileImage
                  : undefined;

                return (
                  <div
                    key={activity.id}
                    className="bg-white px-4 py-4 transition-colors hover:bg-gray-50"
                  >
                    <div className="flex items-start gap-3">
                      <div className="shrink-0">
                        {hasUser ? (
                          <Avatar
                            size="sm"
                            src={avatarSrc}
                            name={actorInitials}
                            className="shrink-0"
                          />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-sm font-semibold text-gray-700">
                            {actorInitials}
                          </div>
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="mb-1 flex items-center gap-2">
                              <span
                                className={`text-sm font-medium ${
                                  hasUser
                                    ? "text-gray-900 cursor-pointer hover:underline"
                                    : "text-gray-500"
                                }`}
                                onClick={() => {
                                  if (activity.performedBy?.id) {
                                    navigate(
                                      `/profile?profileId=${activity.performedBy.id}`,
                                    );
                                  }
                                }}
                              >
                                {actorName}
                              </span>
                            </div>

                            <div
  className="break-words text-sm leading-relaxed text-gray-700"
  dangerouslySetInnerHTML={{
    __html: getActivityDisplayMessage(activity),
  }}
/>
                          </div>

                          <Chip
                            size="sm"
                            color="primary"
                            variant="flat"
                            className="shrink-0 text-[10px] uppercase"
                          >
                            {getActivityStatusLabel(activity.status, t)}
                          </Chip>
                        </div>

                        <span className="mt-1 block text-xs text-gray-500">
                          {new Date(activity.createdAt + "Z").toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default GeneralActivityTab;